<template>
    <div class="pb-10">
        <div class="flex items-center justify-between gap-4 mb-3">
            <PageTitle title="Retraits de stock" icon="fas fa-box-open" />
            <!-- Bouton Nouveau retrait -->
            <button
                v-permission="'stock-withdrawals.create'"
                class="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
                @click="onNewWithdrawal">
                <i class="fas fa-plus text-xs"></i>
                Nouveau retrait
            </button>
        </div>
        <!-- Filtres -->
        <BaseCard class="mb-5">
            <div class="flex flex-wrap gap-3 items-end">
                <!-- Recherche -->
                <div class="flex-1 min-w-[220px]">
                    <div class="relative">
                        <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                        <input v-model="searchQuery" type="text" placeholder="Rechercher par référence..."
                            class="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-gray-50 transition-colors"
                            @input="onSearchChange" />
                        <button v-if="searchQuery"
                            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            @click="searchQuery = ''; onSearchChange()">
                            <i class="fas fa-times text-[10px]"></i>
                        </button>
                    </div>
                </div>

                <!-- Date début -->
                <div class="w-40">
                    <input v-model="startDate" type="date"
                        class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-gray-50 transition-colors"
                        @input="onDateChange" />
                </div>

                <!-- Date fin -->
                <div class="w-40">
                    <input v-model="endDate" type="date"
                        class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-gray-50 transition-colors"
                        @input="onDateChange" />
                </div>

                <!-- Statut -->
                <div class="w-40">
                    <div class="relative">
                        <i class="fas fa-circle-dot absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                        <select v-model="selectedStatus" @change="onStatusChange"
                            class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-gray-50 transition-colors appearance-none">
                            <option value="">Tous statuts</option>
                            <option value="draft">Brouillon</option>
                            <option value="completed">Complété</option>
                            <option value="cancelled">Annulé</option>
                        </select>
                        <i
                            class="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none"></i>
                    </div>
                </div>

                <!-- Raison -->
                <div class="w-44">
                    <div class="relative">
                        <i class="fas fa-tag absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                        <select v-model="selectedReason" @change="onReasonChange"
                            class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-gray-50 transition-colors appearance-none">
                            <option value="">Toutes raisons</option>
                            <option value="damage">Dommage/Casse</option>
                            <option value="loss">Perte</option>
                            <option value="theft">Vol</option>
                            <option value="expired">Expiré</option>
                            <option value="return_to_supplier">Retour fournisseur</option>
                            <option value="adjustment">Ajustement</option>
                            <option value="other">Autre</option>
                        </select>
                        <i
                            class="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none"></i>
                    </div>
                </div>
            </div>
        </BaseCard>

        <!-- Filtres actifs -->
        <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 mb-5">
            <span class="text-xs text-gray-400">Filtres:</span>
            <span v-if="searchQuery"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                "{{ searchQuery }}"
                <button @click="searchQuery = ''; onSearchChange()" class="hover:text-blue-900 transition-colors">
                    <i class="fas fa-times text-[9px]"></i>
                </button>
            </span>
            <span v-if="startDate"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-violet-50 text-violet-700 rounded-full text-xs font-medium">
                Début: {{ startDate }}
                <button @click="startDate = ''; onDateChange()" class="hover:text-violet-900 transition-colors">
                    <i class="fas fa-times text-[9px]"></i>
                </button>
            </span>
            <span v-if="endDate"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-violet-50 text-violet-700 rounded-full text-xs font-medium">
                Fin: {{ endDate }}
                <button @click="endDate = ''; onDateChange()" class="hover:text-violet-900 transition-colors">
                    <i class="fas fa-times text-[9px]"></i>
                </button>
            </span>
            <span v-if="selectedStatus"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                {{ getStatusLabel(selectedStatus) }}
                <button @click="selectedStatus = ''; onStatusChange()" class="hover:text-emerald-900 transition-colors">
                    <i class="fas fa-times text-[9px]"></i>
                </button>
            </span>
            <span v-if="selectedReason"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
                {{ getReasonLabel(selectedReason) }}
                <button @click="selectedReason = ''; onReasonChange()" class="hover:text-amber-900 transition-colors">
                    <i class="fas fa-times text-[9px]"></i>
                </button>
            </span>
            <button class="text-xs text-gray-400 hover:text-gray-600 ml-auto transition-colors"
                @click="clearAllFilters">
                Tout effacer
            </button>
        </div>

        <!-- Chargement -->
        <BaseCard v-if="loading" padding="none">
            <div class="flex flex-col items-center justify-center py-16">
                <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
                <p class="text-sm text-gray-400">Chargement des retraits...</p>
            </div>
        </BaseCard>

        <!-- État vide -->
        <BaseCard v-else-if="withdrawals.length === 0" padding="none">
            <div class="flex flex-col items-center justify-center py-16 text-center px-6">
                <div class="text-gray-300 mb-4">
                    <i class="fas fa-box-open text-5xl"></i>
                </div>
                <h3 class="text-base font-semibold text-gray-700 mb-1">Aucun retrait trouvé</h3>
                <p class="text-sm text-gray-400 max-w-sm mb-6">
                    <span v-if="hasActiveFilters">Aucun retrait ne correspond à vos critères.</span>
                    <span v-else>Aucun retrait enregistré pour le moment.</span>
                </p>
                <button v-if="hasActiveFilters"
                    class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
                    @click="clearAllFilters">
                    Réinitialiser les filtres
                </button>
                <button v-else
                    v-permission="'stock-withdrawals.create'"
                    class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
                    @click="onNewWithdrawal">
                    <i class="fas fa-plus text-xs"></i>
                    Nouveau retrait
                </button>
            </div>
        </BaseCard>

        <!-- Tableau -->
        <BaseCard v-else padding="none">
            <div class="overflow-x-auto">
                <table class="w-full">
                    <thead>
                        <tr class="bg-gray-50/80 border-b border-gray-200">
                            <th
                                class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Référence
                            </th>
                            <th
                                class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Date
                            </th>
                            <th
                                class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Raison
                            </th>
                            <th
                                class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Statut
                            </th>
                            <th
                                class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Éléments
                            </th>
                            <th
                                class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Créé par
                            </th>
                            <th
                                class="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="withdrawal in withdrawals" :key="withdrawal.id"
                            class="hover:bg-gray-50/80 transition-colors cursor-pointer"
                            @click="viewDetails(withdrawal)">
                            <!-- Référence -->
                            <td class="px-5 py-3.5">
                                <router-link :to="{ name: 'stock-withdrawal-details', params: { id: withdrawal.id } }"
                                    @click.stop class="text-sm font-semibold text-primary hover:underline">
                                    {{ withdrawal.reference }}
                                </router-link>
                            </td>

                            <!-- Date -->
                            <td class="px-5 py-3.5">
                                <span class="text-sm text-gray-700">{{ formatDate(withdrawal.withdrawal_date) }}</span>
                            </td>

                            <!-- Raison -->
                            <td class="px-5 py-3.5">
                                <span :class="[
                                    'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
                                    getReasonBadgeClass(withdrawal.reason)
                                ]">
                                    {{ getReasonLabel(withdrawal.reason) }}
                                </span>
                            </td>

                            <!-- Statut -->
                            <td class="px-5 py-3.5">
                                <span :class="[
                                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold',
                                    getStatusBadgeClass(withdrawal.status)
                                ]">
                                    <span class="w-1.5 h-1.5 rounded-full" :class="{
                                        'bg-gray-400': withdrawal.status === 'draft',
                                        'bg-emerald-500': withdrawal.status === 'completed',
                                        'bg-red-500': withdrawal.status === 'cancelled'
                                    }"></span>
                                    {{ getStatusLabel(withdrawal.status) }}
                                </span>
                            </td>

                            <!-- Éléments -->
                            <td class="px-5 py-3.5">
                                <div class="flex flex-col">
                                    <span class="text-sm font-medium text-gray-900">
                                        {{ withdrawal.items?.length || 0 }} article(s)
                                    </span>
                                    <span class="text-[11px] text-gray-400">
                                        {{ calculateTotalQuantity(withdrawal.items) }} unité(s)
                                    </span>
                                </div>
                            </td>

                            <!-- Créé par -->
                            <td class="px-5 py-3.5">
                                <span class="text-sm text-gray-700">{{ withdrawal.user?.name || '-' }}</span>
                            </td>

                            <!-- Actions -->
                            <td class="px-5 py-3.5">
                                <div class="flex items-center justify-end gap-1" @click.stop>
                                    <button
                                        class="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Télécharger PDF" :disabled="downloadingPdf[withdrawal.id]"
                                        @click="downloadPdf(withdrawal)">
                                        <span v-if="downloadingPdf[withdrawal.id]"
                                            class="w-4 h-4 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin block"></span>
                                        <i v-else class="fas fa-file-pdf text-sm"></i>
                                    </button>
                                    <button
                                        class="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Télécharger CSV" :disabled="downloadingCsv[withdrawal.id]"
                                        @click="downloadCsv(withdrawal)">
                                        <span v-if="downloadingCsv[withdrawal.id]"
                                            class="w-4 h-4 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin block"></span>
                                        <i v-else class="fas fa-file-csv text-sm"></i>
                                    </button>
                                    <button
                                        class="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                        title="Télécharger Excel" :disabled="downloadingExcel[withdrawal.id]"
                                        @click="downloadExcel(withdrawal)">
                                        <span v-if="downloadingExcel[withdrawal.id]"
                                            class="w-4 h-4 border-2 border-gray-200 border-t-green-500 rounded-full animate-spin block"></span>
                                        <i v-else class="fas fa-file-excel text-sm"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="withdrawals.length > 0" class="border-t border-gray-100 px-5 py-4">
                <PaginationComponent :current-page="currentPage" :per-page="perPage" :total="total" :from="from"
                    :to="to" :show-page-numbers="true" :max-visible-pages="5" @page-change="onPageChange" />
            </div>
        </BaseCard>
    </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import StockWithdrawalService from '@/services/stockWithdrawalService'
import { useToastStore } from '@/store/modules/toastStore'
import { useRouter } from 'vue-router'
import PageTitle from '@/components/common/PageTitle.vue'

export default {
    name: 'StockWithdrawalList',
    components: {
        PageTitle,
        PaginationComponent,
        BaseCard
    },
    props: {
        perPageDefault: {
            type: Number,
            default: 15
        }
    },
    emits: ['new-withdrawal'],
    setup(props) {
        const toastStore = useToastStore()
        const router = useRouter()
        const withdrawals = ref([])
        const loading = ref(false)
        const startDate = ref('')
        const endDate = ref('')
        const selectedStatus = ref('')
        const selectedReason = ref('')
        const searchQuery = ref('')
        const currentPage = ref(1)
        const perPage = ref(props.perPageDefault)
        const total = ref(0)
        const from = ref(0)
        const to = ref(0)
        const downloadingPdf = ref({})
        const downloadingCsv = ref({})
        const downloadingExcel = ref({})
        const searchTimeout = ref(null)

        const hasActiveFilters = computed(() => {
            return startDate.value || endDate.value || selectedStatus.value || selectedReason.value || searchQuery.value
        })

        const fetchWithdrawals = async () => {
            loading.value = true
            try {
                const params = {
                    page: currentPage.value,
                    per_page: perPage.value
                }

                if (startDate.value) params.start_date = startDate.value
                if (endDate.value) params.end_date = endDate.value
                if (selectedStatus.value) params.status = selectedStatus.value
                if (selectedReason.value) params.reason = selectedReason.value
                if (searchQuery.value) params.search = searchQuery.value

                const response = await StockWithdrawalService.getStockWithdrawals(params)

                if (response.data && response.data.data) {
                    withdrawals.value = response.data.data
                    if (response.data.meta) {
                        currentPage.value = response.data.meta.current_page || currentPage.value
                        perPage.value = response.data.meta.per_page || perPage.value
                        total.value = response.data.meta.total || 0
                        from.value = response.data.meta.from ?? ((response.data.meta.current_page || 1) - 1) * (response.data.meta.per_page || perPage.value) + 1
                        to.value = response.data.meta.to ?? Math.min((response.data.meta.current_page || 1) * (response.data.meta.per_page || perPage.value), response.data.meta.total || 0)
                    }
                } else {
                    withdrawals.value = response.data || []
                }
            } catch (error) {
                console.error('Erreur lors du chargement des retraits:', error)
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des retraits de stock',
                    duration: 4000
                })
            } finally {
                loading.value = false
            }
        }

        const onDateChange = () => {
            currentPage.value = 1
            fetchWithdrawals()
        }

        const onStatusChange = () => {
            currentPage.value = 1
            fetchWithdrawals()
        }

        const onReasonChange = () => {
            currentPage.value = 1
            fetchWithdrawals()
        }

        const onSearchChange = () => {
            currentPage.value = 1
            clearTimeout(searchTimeout.value)
            searchTimeout.value = setTimeout(() => {
                fetchWithdrawals()
            }, 500)
        }

        const clearAllFilters = () => {
            startDate.value = ''
            endDate.value = ''
            selectedStatus.value = ''
            selectedReason.value = ''
            searchQuery.value = ''
            currentPage.value = 1
            fetchWithdrawals()
        }

        const onPageChange = (page) => {
            currentPage.value = page
            fetchWithdrawals()
        }

        const onNewWithdrawal = () => {
            router.push({ name: 'create-stock-withdrawal' })
        }

        const viewDetails = (withdrawal) => {
            router.push({ name: 'stock-withdrawal-details', params: { id: withdrawal.id } })
        }

        const formatDate = (date) => {
            return StockWithdrawalService.formatDateTime(date)
        }

        const getReasonLabel = (reason) => {
            return StockWithdrawalService.getReasonLabel(reason)
        }

        const getStatusLabel = (status) => {
            return StockWithdrawalService.getStatusLabel(status)
        }

        const getReasonBadgeClass = (reason) => {
            const classes = {
                'damage': 'bg-amber-50 text-amber-700',
                'loss': 'bg-amber-50 text-amber-700',
                'expired': 'bg-orange-50 text-orange-700',
                'theft': 'bg-red-50 text-red-700',
                'return_to_supplier': 'bg-blue-50 text-blue-700',
                'adjustment': 'bg-gray-100 text-gray-600',
                'other': 'bg-gray-100 text-gray-600'
            }
            return classes[reason] || 'bg-gray-100 text-gray-600'
        }

        const getStatusBadgeClass = (status) => {
            const classes = {
                'draft': 'bg-gray-100 text-gray-600',
                'completed': 'bg-emerald-50 text-emerald-700',
                'cancelled': 'bg-red-50 text-red-700'
            }
            return classes[status] || 'bg-gray-100 text-gray-600'
        }

        const calculateTotalQuantity = (items) => {
            return StockWithdrawalService.calculateTotalQuantity(items || [])
        }

        const downloadPdf = async (withdrawal) => {
            downloadingPdf.value[withdrawal.id] = true
            try {
                await StockWithdrawalService.downloadStockWithdrawalPdf(withdrawal.id)
                toastStore.addToast({
                    type: 'success',
                    message: 'PDF téléchargé avec succès',
                    duration: 3000
                })
            } catch (error) {
                console.error('Erreur lors du téléchargement PDF:', error)
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors du téléchargement PDF',
                    duration: 4000
                })
            } finally {
                downloadingPdf.value[withdrawal.id] = false
            }
        }

        const downloadCsv = async (withdrawal) => {
            downloadingCsv.value[withdrawal.id] = true
            try {
                await StockWithdrawalService.exportStockWithdrawal(withdrawal.id, { format: 'csv' })
                toastStore.addToast({
                    type: 'success',
                    message: 'Fichier CSV téléchargé avec succès',
                    duration: 3000
                })
            } catch (error) {
                console.error('Erreur lors du téléchargement CSV:', error)
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors du téléchargement CSV',
                    duration: 4000
                })
            } finally {
                downloadingCsv.value[withdrawal.id] = false
            }
        }

        const downloadExcel = async (withdrawal) => {
            downloadingExcel.value[withdrawal.id] = true
            try {
                await StockWithdrawalService.exportStockWithdrawal(withdrawal.id, { format: 'excel' })
                toastStore.addToast({
                    type: 'success',
                    message: 'Fichier Excel téléchargé avec succès',
                    duration: 3000
                })
            } catch (error) {
                console.error('Erreur lors du téléchargement Excel:', error)
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors du téléchargement Excel',
                    duration: 4000
                })
            } finally {
                downloadingExcel.value[withdrawal.id] = false
            }
        }

        onMounted(() => {
            fetchWithdrawals()
        })

        onUnmounted(() => {
            if (searchTimeout.value) {
                clearTimeout(searchTimeout.value)
            }
        })

        return {
            withdrawals,
            loading,
            startDate,
            endDate,
            selectedStatus,
            selectedReason,
            searchQuery,
            currentPage,
            perPage,
            total,
            from,
            to,
            downloadingPdf,
            downloadingCsv,
            downloadingExcel,
            hasActiveFilters,
            onDateChange,
            onStatusChange,
            onReasonChange,
            onSearchChange,
            onPageChange,
            clearAllFilters,
            onNewWithdrawal,
            viewDetails,
            formatDate,
            getReasonLabel,
            getStatusLabel,
            getReasonBadgeClass,
            getStatusBadgeClass,
            calculateTotalQuantity,
            downloadPdf,
            downloadCsv,
            downloadExcel
        }
    }
}
</script>
